/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useParams } from 'js/lib/useRouter';

import { Button, Divider, Typography2, breakpoints } from '@coursera/cds-core';
import { Link as TrackedLink, useTracker } from '@coursera/event-pulse/react';

import type { SkillMasteryLevelFragment } from 'bundles/levelsets/levelsets-v2/api/__generated__/SkillMasteryLevelsForCurrentUser';
import { getSkillsMasteryLevelLabels } from 'bundles/levelsets/levelsets-v2/constants';
import type { Skill } from 'bundles/levelsets/levelsets-v2/types';
import { getLatestMasteryProgress, getMasteryLevelNumber } from 'bundles/levelsets/levelsets-v2/utils/formatConversion';
import { generateSkillsPageUrl } from 'bundles/skills-common/private/utils/skillLinkUtils';

import _t from 'i18n!nls/levelsets';

type MasteryLevel = SkillMasteryLevelFragment['masteryLevel'];

type Props = {
  skill: Skill;
  masteryLevel?: MasteryLevel;
  masteryPercent?: number;
  variant?: 'primary' | 'forSkillPage';
  index?: number;
};

// If the background image is in a separate file, cards will appear with a white background and eventually get the
// background image when the browser decides to fetch it. By including it here it's compressed with the JS and the card
// will appear with the background image immediately.
const backgroundSvg = `<svg width="121" height="131" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M137.442 11.676 68.721-28 0 11.676v79.352l68.721 39.676 68.721-39.676V11.676Zm-18.957 10.947L68.722-6.108l-49.764 28.73v57.463l49.764 28.73 49.763-28.73V22.623Z" fill="var(--cds-color-blue-25)"/><path d="m68.532 15.283 11.025 20.895 23.279 4.028L86.37 57.148l3.363 23.385-21.2-10.425L47.33 80.533l3.363-23.385-16.466-16.942 23.28-4.028 11.024-20.895Z" fill="var(--cds-color-blue-25)"/></svg>`;

const styles = {
  card: css`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-width: 312px;
    min-height: 148px;
    padding: var(--cds-spacing-300) var(--cds-spacing-300) 20px var(--cds-spacing-300);
    border-radius: 16px;
    border: 2px solid var(--cds-color-grey-50);
    background-color: var(--cds-color-neutral-background-primary);
    background-image: url(data:image/svg+xml;base64,${btoa(backgroundSvg)});
    background-repeat: no-repeat;
    background-position: right 0 top 0;

    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-200) var(--cds-spacing-150) var(--cds-spacing-100) var(--cds-spacing-150);
    }
  `,
  cardHeading: css`
    ${breakpoints.down('sm')} {
      font-size: 16px;
    }
  `,
  cardSubHeading: css`
    margin-top: var(--cds-spacing-200);
    margin-bottom: var(--cds-spacing-50);
  `,
  actionLinksNoMastery: (reducedMargins: boolean) => css`
    ${reducedMargins
      ? `margin-top: var(--cds-spacing-100);`
      : `margin-top: var(--cds-spacing-400);
        margin-bottom: var(--cds-spacing-100);`}
    display: flex;
    align-items: flex-start;
    align-content: flex-start;
    gap: var(--cds-spacing-300);
    flex-wrap: wrap;
  `,
  actionLinkPrimary: css`
    padding: 0;

    &:hover {
      text-decoration: none;
    }
  `,
  actionLinkSecondary: css`
    padding: 0;

    &:hover {
      text-decoration: none;
      background-color: transparent;
    }
  `,
  divider: css`
    margin-top: var(--cds-spacing-200);
  `,
  actionLinksWithMastery: css`
    margin-top: var(--cds-spacing-150);
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
  `,
  actionLinkUnderlined: css`
    text-decoration: underline;
    font-weight: 400;
    padding: 0;
    color: var(--cds-color-neutral-primary);

    &:hover {
      background-color: transparent;
    }
  `,
  currentMasteryContainer: css`
    margin-top: var(--cds-spacing-300);
    display: flex;
    gap: var(--cds-spacing-150);
  `,
};

const getEvaluateButtonText = (variant: Props['variant'], masteryPercent?: number) => {
  if (variant === 'forSkillPage') {
    return _t('Assess mastery level');
  }
  return masteryPercent ? _t('Re-attempt') : _t('Evaluate');
};

export const LevelSetCard = ({ skill, masteryLevel, masteryPercent, variant = 'primary', index }: Props) => {
  const track = useTracker();
  const { programSlug } = useParams();

  const attemptUrl = `/levelsets/assessment/${skill.skillId}?programSlug=${programSlug}`;
  // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  const recommendationsUrl = `${generateSkillsPageUrl(skill.skillId, programSlug)}?mastery=true`;

  const { attainedMasteryLevel } = getLatestMasteryProgress(masteryLevel, masteryPercent);

  const masteryLevelLabels = getSkillsMasteryLevelLabels();
  const MasteryLevelIcon = attainedMasteryLevel
    ? masteryLevelLabels[attainedMasteryLevel].icon
    : () => <React.Fragment />;

  const trackedSkill = {
    id: skill.skillId,
    name: skill.skillName,
  };
  const trackingData = {
    skill: trackedSkill,
    enterprise: {
      programSlug,
    },
    ...(variant === 'primary' ? { carousel: { id: 'levelsets' } } : {}),
    levelsetCard: {
      levelsetCardVariant: variant,
      index,
      hasLearnerAttemptedBefore: !!masteryLevel,
      learnerMasteryLevel: getMasteryLevelNumber(attainedMasteryLevel),
      skill: trackedSkill,
    },
  };

  // Skill page won't link to itself:
  const viewRecommendationsButton =
    variant === 'forSkillPage' ? null : (
      <div>
        <Button
          component="a"
          variant="ghost"
          size="small"
          css={styles.actionLinkSecondary}
          href={recommendationsUrl}
          onClick={() => {
            track('click_view_skill_recommendations', {
              ...trackingData,
              button: { name: 'levelsets2_card_view_recommendations' },
            });
          }}
        >
          {_t('View recommendations')}
        </Button>
      </div>
    );

  return (
    <div css={styles.card}>
      <div>
        <Typography2 variant="subtitleLarge" component="h2" css={styles.cardHeading}>
          {variant === 'forSkillPage' && attainedMasteryLevel ? _t('Your mastery level') : skill.skillName}
        </Typography2>

        {attainedMasteryLevel && (
          <div css={styles.currentMasteryContainer}>
            <div>
              <MasteryLevelIcon />
            </div>
            <div>
              <Typography2 component="p" variant="bodySecondary">
                {masteryLevelLabels[attainedMasteryLevel].name}
              </Typography2>
              <Typography2 component="p" variant="subtitleMedium">
                {masteryLevelLabels[attainedMasteryLevel].description}
              </Typography2>
            </div>
          </div>
        )}

        {!attainedMasteryLevel && variant === 'forSkillPage' && (
          <React.Fragment>
            <Typography2 component="h3" variant="subtitleMedium" css={styles.cardSubHeading}>
              {_t('Where do you stand?')}
            </Typography2>
            <Typography2 component="p" variant="bodySecondary">
              {_t(
                'Assess your #{skillName} skills in minutes and we’ll personalize recommendations to match your needs',
                { skillName: skill.skillName }
              )}
            </Typography2>
          </React.Fragment>
        )}
      </div>

      {!attainedMasteryLevel && (
        <div css={styles.actionLinksNoMastery(variant === 'forSkillPage')}>
          <div>
            <Button
              variant={variant === 'forSkillPage' ? 'secondary' : 'primary'}
              size="small"
              component="a"
              href={attemptUrl}
              onClick={() => {
                track('click_assess_skill_with_levelset', {
                  ...trackingData,
                  button: { name: 'levelsets2_card_attempt', linkType: 'internal', linkURL: attemptUrl },
                });
              }}
            >
              {getEvaluateButtonText(variant, masteryPercent)}
            </Button>
          </div>
          {viewRecommendationsButton}
        </div>
      )}

      {attainedMasteryLevel && (
        <React.Fragment>
          <Divider color="light" css={styles.divider} />
          <div css={styles.actionLinksWithMastery}>
            {viewRecommendationsButton}
            <div>
              <Button
                variant="ghost"
                size="small"
                component="a"
                href={attemptUrl}
                css={variant === 'primary' ? styles.actionLinkUnderlined : styles.actionLinkPrimary}
                onClick={() => {
                  track('click_assess_skill_with_levelset', {
                    ...trackingData,
                    button: { name: 'levelsets2_card_reattempt', linkType: 'internal', linkURL: attemptUrl },
                  });
                }}
              >
                {_t('Re-attempt')}
              </Button>
            </div>
            {variant === 'forSkillPage' && (
              <div>
                <Button
                  variant="ghost"
                  size="small"
                  component={TrackedLink}
                  href="https://docs.google.com/document/d/1feHy5_fS4fHAcD6VGi2mVfEAa5ExgS05dhDqe09Pb80"
                  target="_blank"
                  css={styles.actionLinkUnderlined}
                  trackingData={{
                    skill: trackedSkill,
                    enterprise: { programSlug },
                    button: { name: 'levelsets2_card_about_mastery_levels' },
                  }}
                >
                  {_t('About mastery levels')}
                </Button>
              </div>
            )}
          </div>
        </React.Fragment>
      )}
    </div>
  );
};
